import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChatAvatar } from "./chat/chatUi";
import {
    CalendarDays, ChevronLeft, ChevronRight, Clock, Copy, Download, ExternalLink, Forward, Loader2, Mail, MapPin,
    Pencil, Plus, Radio, Repeat, Video, X, XCircle
} from "@fg/icons";
import { connectParityApi, normalizeUserId, type Meeting } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useCancelMeeting, useMeetings } from "@/api/hooks";
import { cn } from "@/lib/utils";
import { isBreakoutSubject } from "@/lib/breakout";
import { calendarLinks } from "@/lib/calendarLinks";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { DEFAULT_TIME_ZONE, formatDay, formatTime, fromZoned, offsetLabel, parseClock, toZoned } from "@/lib/timezone";
import { MeetingScheduler } from "./MeetingScheduler";
import { MeetingRecordingsSection } from "./recording/MeetingRecordings";
import { MeetingChatThread } from "./meeting/MeetingChatThread";
import { MeetingRecap } from "./meeting/MeetingRecap";
import { MeetingNotes } from "./meeting/MeetingNotes";
import {
    mailCalendarApi, mailMessageUrl, mailWebUrl, useMailCalendarSettings, type MailCalendarEvent
} from "./meeting/mailCalendarApi";
import { WorkingHoursDialog } from "./meeting/WorkingHoursDialog";

type View = "day" | "workweek" | "week" | "month";

/** Height of one hour in the day/week grid. */
const HOUR_PX = 48;
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * One thing on the grid: a Connect meeting or an entry from the user's Mail
 * calendar. `start`/`end` are "zoned wall" Dates — their local fields read as
 * the wall clock in the calendar's time zone (see lib/timezone toZoned), so the
 * grid maths can use getHours()/getDate() whatever zone the browser is in.
 */
interface CalEvent {
    key: string;
    start: Date;
    end: Date;
    title: string;
    allDay: boolean;
    meeting?: Meeting;
    mail?: MailCalendarEvent;
}

/** Working hours in minutes after midnight, and ISO working days. */
interface WorkHours {
    days: number[];
    start: number;
    end: number;
}

/**
 * Connect calendar — Day / Week / Month. Meetings are drawn at
 * their real length (overlaps side by side), with a now-line, and clicking one
 * opens its details instead of joining straight away: Join, and for the host
 * Edit (re-sends the invitation) and Cancel (sends a cancellation). Built with
 * plain Tailwind — Syncfusion's Schedule is licensed for Mail only.
 * <p>
 * It also shows the user's Mail calendar (read from the Mail BFF, repeating
 * events expanded there), dashed and indigo, read-only with "Open in Mail". An
 * emailed copy of a Connect meeting appears once — as the meeting. Times are in
 * the zone from the shared calendar settings, and working hours are shaded.
 */
export function CalendarView() {
    const token = useAccessToken();
    const auth = useAuth();
    const myUserId = normalizeUserId(auth.user?.profile.sub);
    const meetings = useMeetings();
    const navigate = useNavigate();
    const settings = useMailCalendarSettings();
    const tz = settings.data?.timeZone ?? DEFAULT_TIME_ZONE;
    const work = useMemo<WorkHours>(() => ({
        days: settings.data?.workDays ?? [1, 2, 3, 4, 5],
        start: parseClock(settings.data?.workStart) ?? 9 * 60,
        end: parseClock(settings.data?.workEnd) ?? 18 * 60
    }), [settings.data]);
    const [hoursOpen, setHoursOpen] = useState(false);

    const [view, setView] = useState<View>("week");
    const [anchor, setAnchor] = useState<Date>(() => startOfDay(toZoned(new Date(), tz)));
    const [scheduling, setScheduling] = useState<{ start: Date | null } | null>(null);

    // The top bar's "New meeting" lands here with ?new=1: open the form, then
    // drop the flag so a refresh or Back doesn't open it again.
    const [params, setParams] = useSearchParams();
    useEffect(() => {
        if (params.get("new") !== "1") return;
        setScheduling({ start: null });
        const next = new URLSearchParams(params);
        next.delete("new");
        setParams(next, { replace: true });
    }, [params, setParams]);
    const [editing, setEditing] = useState<Meeting | null>(null);
    const [selected, setSelected] = useState<Meeting | null>(null);
    const [selectedMail, setSelectedMail] = useState<MailCalendarEvent | null>(null);

    const days = useMemo(() => {
        if (view === "day") return [anchor];
        if (view === "week" || view === "workweek") {
            const s = startOfWeek(anchor);
            return Array.from({ length: view === "workweek" ? 5 : 7 }, (_, i) => addDays(s, i));
        }
        const s = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
        return Array.from({ length: 42 }, (_, i) => addDays(s, i));
    }, [view, anchor]);

    // The Mail calendar for exactly the range on screen.
    const rangeFrom = fromZoned(days[0]!, tz);
    const rangeTo = fromZoned(addDays(days[days.length - 1]!, 1), tz);
    const mailEvents = useQuery({
        queryKey: ["mail-calendar-events", rangeFrom.getTime(), rangeTo.getTime(), token],
        queryFn: () => mailCalendarApi.listEvents(rangeFrom, rangeTo, token),
        enabled: Boolean(token),
        staleTime: 60_000,
        retry: 1,
        placeholderData: keepPreviousData
    });

    const events = useMemo<CalEvent[]>(() => {
        const list: CalEvent[] = [];
        const meetingIds = new Set<string>();
        for (const m of meetings.data ?? []) {
            meetingIds.add(m.id.toLowerCase());
            // Breakout rooms are working spaces of a running meeting, not calendar entries.
            if (isBreakoutSubject(m.subject)) continue;
            const start = new Date(m.scheduledStart);
            const end = m.scheduledEnd ? new Date(m.scheduledEnd) : new Date(start.getTime() + 30 * 60_000);
            list.push({
                key: `m-${m.id}`,
                start: toZoned(start, tz),
                end: toZoned(end > start ? end : new Date(start.getTime() + 30 * 60_000), tz),
                title: m.subject,
                allDay: false,
                meeting: m
            });
        }
        for (const e of mailEvents.data ?? []) {
            // Shown once: the emailed copy of a Connect meeting gives way to the
            // meeting itself (only when Connect actually answered).
            if (meetings.data && e.connectMeetingId && meetingIds.has(e.connectMeetingId.toLowerCase())) continue;
            const start = new Date(e.startUtc);
            const end = new Date(e.endUtc);
            list.push({
                key: `e-${e.id}-${e.startUtc}`,
                start: toZoned(start, tz),
                end: toZoned(end > start ? end : new Date(start.getTime() + 30 * 60_000), tz),
                title: e.subject,
                allDay: e.isAllDay,
                mail: e
            });
        }
        return list;
    }, [meetings.data, mailEvents.data, tz]);

    const open = (ev: CalEvent) => {
        if (ev.meeting) setSelected(ev.meeting);
        else if (ev.mail) setSelectedMail(ev.mail);
    };

    const step = (dir: 1 | -1) =>
        setAnchor((a) => view === "day" ? addDays(a, dir)
            : view === "week" || view === "workweek" ? addDays(a, 7 * dir)
            : new Date(a.getFullYear(), a.getMonth() + dir, 1));

    const rangeLabel = view === "day"
        ? anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : view === "week" || view === "workweek"
            ? formatRange(days[0]!, days[days.length - 1]!)
            : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

    const isHost = (m: Meeting) => normalizeUserId(m.hostUserId) === myUserId;

    return (
        <section className="flex min-h-0 flex-1 flex-col p-2 sm:p-4">
            <header className="mb-3 flex flex-wrap items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" aria-hidden />
                <h1 className="text-lg font-semibold">Calendar</h1>
                <span className="text-sm text-muted-foreground">{rangeLabel}</span>
                <button
                    type="button"
                    onClick={() => setHoursOpen(true)}
                    className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                    title="Working hours and time zone"
                >
                    <Clock className="h-3 w-3" aria-hidden /> {tz} ({offsetLabel(tz)})
                </button>
                <div className="ml-auto flex flex-wrap items-center gap-1">
                    <div className="mr-2 flex overflow-hidden rounded-md border" role="tablist" aria-label="Calendar view">
                        {(["day", "workweek", "week", "month"] as const).map((v) => (
                            <button
                                key={v}
                                type="button"
                                role="tab"
                                aria-selected={view === v}
                                onClick={() => setView(v)}
                                className={cn(
                                    "h-8 px-3 text-xs font-medium capitalize",
                                    view === v ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                                )}
                            >
                                {v === "workweek" ? "Work week" : v}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setScheduling({ start: null })}
                        className="mr-1 flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    >
                        <Plus className="h-3.5 w-3.5" /> New meeting
                    </button>
                    <button type="button" onClick={() => step(-1)} className="flex h-8 w-8 items-center justify-center rounded-md border hover:bg-accent" aria-label="Previous">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setAnchor(startOfDay(toZoned(new Date(), tz)))} className="h-8 rounded-md border px-3 text-xs hover:bg-accent">
                        Today
                    </button>
                    <button type="button" onClick={() => step(1)} className="flex h-8 w-8 items-center justify-center rounded-md border hover:bg-accent" aria-label="Next">
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </header>
            <div className="mb-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border-l-[3px] border-primary bg-primary/15" aria-hidden /> Fowlguard Meeting</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border border-dashed border-indigo-500 bg-indigo-500/10" aria-hidden /> Mail calendar</span>
                {mailEvents.isError ? <span className="text-destructive">Mail calendar couldn't be loaded</span> : null}
                {mailEvents.isFetching && !mailEvents.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Refreshing" /> : null}
            </div>

            {view === "month" ? (
                <MonthGrid
                    days={days}
                    month={anchor.getMonth()}
                    events={events}
                    today={toZoned(new Date(), tz)}
                    onOpen={open}
                    onPickDay={(d) => { setAnchor(d); setView("day"); }}
                    onSlot={(d) => { const s = new Date(d); s.setHours(9, 0, 0, 0); setScheduling({ start: fromZoned(s, tz) }); }}
                />
            ) : (
                <TimeGrid
                    days={days}
                    events={events}
                    tz={tz}
                    work={work}
                    onOpen={open}
                    onSlot={(wall) => setScheduling({ start: fromZoned(wall, tz) })}
                />
            )}

            {selected ? (
                <MeetingDetails
                    meeting={selected}
                    isHost={isHost(selected)}
                    token={token}
                    tz={tz}
                    myEmail={(auth.user?.profile.email ?? "").toLowerCase()}
                    onClose={() => setSelected(null)}
                    onEdit={() => { setEditing(selected); setSelected(null); }}
                    onJoin={async () => {
                        const m = selected;
                        try {
                            if (m.status === "Scheduled" && isHost(m)) await connectParityApi.startMeeting(m.id, token);
                            await connectParityApi.joinMeeting(m.id, token);
                            // The meeting route keeps the id in the address bar, so the link
                            // can be shared and a refresh rejoins.
                            navigate(`/meeting/${m.id}`);
                        } catch (err) {
                            console.warn("Calendar join failed", err);
                        }
                    }}
                />
            ) : null}
            {selectedMail ? <MailEventDetails event={selectedMail} tz={tz} onClose={() => setSelectedMail(null)} /> : null}

            {scheduling ? (
                <MeetingScheduler
                    initialStart={scheduling.start}
                    // A slot click passes its start time; the New meeting
                    // buttons pass none and must be closed deliberately.
                    dismissOnBackdrop={scheduling.start !== null}
                    onClose={() => setScheduling(null)}
                    onCreated={() => { void meetings.refetch(); }}
                />
            ) : null}
            {editing ? (
                <MeetingScheduler
                    initialStart={null}
                    meeting={editing}
                    dismissOnBackdrop={false}
                    onClose={() => setEditing(null)}
                    onSaved={() => { void meetings.refetch(); }}
                />
            ) : null}
            <WorkingHoursDialog open={hoursOpen} onOpenChange={setHoursOpen} settings={settings.data} />
        </section>
    );
}

// ── Day / Week ───────────────────────────────────────────────────────────────

function TimeGrid({
    days,
    events,
    tz,
    work,
    onOpen,
    onSlot
}: {
    days: Date[];
    events: CalEvent[];
    tz: string;
    work: WorkHours;
    onOpen: (ev: CalEvent) => void;
    onSlot: (start: Date) => void;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    // Open at the start of the working day, rather than midnight.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = Math.max(0, (work.start / 60 - 1) * HOUR_PX);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = window.setInterval(() => setNow(new Date()), 60_000);
        return () => window.clearInterval(t);
    }, []);
    const nowWall = toZoned(now, tz);
    const allDay = events.filter((e) => e.allDay);

    const columns = `56px repeat(${days.length}, minmax(0, 1fr))`;
    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card">
            <div className="grid shrink-0 border-b" style={{ gridTemplateColumns: columns }}>
                <div className="border-r" />
                {days.map((d) => {
                    const today = sameDay(d, nowWall);
                    const dayStart = startOfDay(d);
                    const dayEnd = addDays(dayStart, 1);
                    const todays = allDay.filter((e) => e.start < dayEnd && e.end > dayStart);
                    return (
                        <div key={d.toISOString()} className={cn("flex min-w-0 flex-col items-center border-r py-2 text-xs", today && "bg-primary/10")}>
                            <span className="text-muted-foreground">{DAY_NAMES[(d.getDay() + 6) % 7]}</span>
                            <span className={cn(
                                "mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full font-semibold",
                                today && "bg-primary text-primary-foreground"
                            )}>
                                {d.getDate()}
                            </span>
                            {todays.slice(0, 2).map((e) => (
                                <button
                                    key={e.key}
                                    type="button"
                                    onClick={() => onOpen(e)}
                                    className={cn("mt-1 w-[calc(100%-6px)] truncate rounded border-l-2 px-1 text-left text-[10px] leading-4", eventClasses(e))}
                                    title={`${e.title} · all day`}
                                >
                                    {e.title}
                                </button>
                            ))}
                            {todays.length > 2 ? <span className="text-[10px] text-muted-foreground">+{todays.length - 2} all day</span> : null}
                        </div>
                    );
                })}
            </div>
            <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
                <div className="grid" style={{ gridTemplateColumns: columns }}>
                    <div className="border-r">
                        {Array.from({ length: 24 }, (_, h) => (
                            <div key={h} className="relative border-b pr-1 text-right text-[10px] text-muted-foreground" style={{ height: HOUR_PX }}>
                                <span className="relative -top-1.5">{h === 0 ? "" : formatHour(h)}</span>
                            </div>
                        ))}
                    </div>
                    {days.map((d) => (
                        <DayColumn key={d.toISOString()} day={d} events={events} now={nowWall} work={work} onOpen={onOpen} onSlot={onSlot} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function DayColumn({
    day,
    events,
    now,
    work,
    onOpen,
    onSlot
}: {
    day: Date;
    events: CalEvent[];
    now: Date;
    work: WorkHours;
    onOpen: (ev: CalEvent) => void;
    onSlot: (start: Date) => void;
}) {
    const dayStart = startOfDay(day);
    const dayEnd = addDays(dayStart, 1);
    const laidOut = useMemo(
        () => layoutDay(
            events
                .filter((e) => !e.allDay && e.start < dayEnd && e.end > dayStart)
                .map((e) => ({ ...e, s: e.start < dayStart ? dayStart : e.start, e2: e.end > dayEnd ? dayEnd : e.end }))
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [events, dayStart.getTime()]
    );
    const isToday = sameDay(day, now);
    const workDay = work.days.includes(((day.getDay() + 6) % 7) + 1);

    return (
        <div
            className={cn("relative border-r", isToday && "bg-primary/[0.03]")}
            style={{ height: 24 * HOUR_PX }}
            onClick={(e) => {
                // Click an empty slot → schedule there, on the half hour.
                const rect = e.currentTarget.getBoundingClientRect();
                const minutes = Math.floor(((e.clientY - rect.top) / HOUR_PX) * 2) * 30;
                const start = new Date(dayStart);
                start.setMinutes(minutes);
                onSlot(start);
            }}
        >
            {/* Outside working hours is shaded. */}
            {workDay ? (
                <>
                    {work.start > 0 ? <div className="pointer-events-none absolute inset-x-0 top-0 bg-muted/50" style={{ height: (work.start / 60) * HOUR_PX }} aria-hidden /> : null}
                    {work.end < 24 * 60 ? <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-muted/50" style={{ top: (work.end / 60) * HOUR_PX }} aria-hidden /> : null}
                </>
            ) : (
                <div className="pointer-events-none absolute inset-0 bg-muted/50" aria-hidden />
            )}
            {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="pointer-events-none absolute inset-x-0 border-b" style={{ top: h * HOUR_PX, height: HOUR_PX }} />
            ))}
            {isToday ? (
                <div
                    className="pointer-events-none absolute inset-x-0 z-20 h-0.5 bg-red-500"
                    style={{ top: minutesOf(now) / 60 * HOUR_PX }}
                    aria-hidden
                >
                    <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
                </div>
            ) : null}
            {laidOut.map(({ item, col, cols }) => {
                const top = minutesOf(item.s) / 60 * HOUR_PX;
                const height = Math.max(((item.e2.getTime() - item.s.getTime()) / 3_600_000) * HOUR_PX, 20);
                return (
                    <button
                        key={item.key}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpen(item); }}
                        className={cn(
                            "absolute z-10 overflow-hidden rounded-md border-l-[3px] px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            eventClasses(item)
                        )}
                        style={{
                            top,
                            height,
                            left: `calc(${(col / cols) * 100}% + 2px)`,
                            width: `calc(${100 / cols}% - 4px)`
                        }}
                        title={`${item.title} · ${timeRange(item.start, item.end)}${item.mail ? " · Mail calendar" : ""}`}
                    >
                        <span className="flex items-center gap-1 font-semibold">
                            <EventIcon ev={item} />
                            <span className="truncate">{item.title}</span>
                        </span>
                        {height >= 34 ? <span className="block truncate opacity-80">{timeRange(item.start, item.end)}</span> : null}
                    </button>
                );
            })}
        </div>
    );
}

type Placed<T> = { item: T; col: number; cols: number };

/**
 * Overlapping meetings share the column width, side by side.
 * Greedy: each meeting takes the first column free at its start; a cluster of
 * mutually-overlapping meetings shares one column count.
 */
function layoutDay<T extends { s: Date; e2: Date }>(items: T[]): Placed<T>[] {
    const sorted = [...items].sort((a, b) => a.s.getTime() - b.s.getTime() || b.e2.getTime() - a.e2.getTime());
    const out: Placed<T>[] = [];
    let cluster: Placed<T>[] = [];
    let colEnds: number[] = [];
    let clusterEnd = -Infinity;
    const flush = () => {
        for (const p of cluster) p.cols = colEnds.length || 1;
        cluster = [];
        colEnds = [];
        clusterEnd = -Infinity;
    };
    for (const item of sorted) {
        const s = item.s.getTime();
        const e = item.e2.getTime();
        if (cluster.length > 0 && s >= clusterEnd) flush();
        let col = colEnds.findIndex((end) => end <= s);
        if (col === -1) {
            col = colEnds.length;
            colEnds.push(e);
        } else {
            colEnds[col] = e;
        }
        const placed: Placed<T> = { item, col, cols: 1 };
        cluster.push(placed);
        out.push(placed);
        clusterEnd = Math.max(clusterEnd, e);
    }
    flush();
    return out;
}

// ── Month ────────────────────────────────────────────────────────────────────

function MonthGrid({
    days,
    month,
    events,
    today,
    onOpen,
    onPickDay,
    onSlot
}: {
    days: Date[];
    month: number;
    events: CalEvent[];
    today: Date;
    onOpen: (ev: CalEvent) => void;
    onPickDay: (d: Date) => void;
    onSlot: (d: Date) => void;
}) {
    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card">
            <div className="grid shrink-0 grid-cols-7 border-b">
                {DAY_NAMES.map((n) => (
                    <div key={n} className="border-r py-1.5 text-center text-xs text-muted-foreground">{n}</div>
                ))}
            </div>
            <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
                {days.map((d) => {
                    const dayStart = startOfDay(d);
                    const dayEnd = addDays(dayStart, 1);
                    const dayEvents = events
                        .filter((e) => (e.allDay ? e.start < dayEnd && e.end > dayStart : sameDay(e.start, d)))
                        .sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.getTime() - b.start.getTime());
                    const inMonth = d.getMonth() === month;
                    return (
                        <div
                            key={d.toISOString()}
                            onClick={() => onSlot(d)}
                            className={cn("flex min-h-0 cursor-pointer flex-col gap-0.5 overflow-hidden border-b border-r p-1 hover:bg-accent/30", !inMonth && "bg-muted/30")}
                        >
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onPickDay(d); }}
                                className={cn(
                                    "flex h-6 w-6 shrink-0 items-center justify-center self-start rounded-full text-xs",
                                    sameDay(d, today) ? "bg-primary font-semibold text-primary-foreground" : inMonth ? "hover:bg-accent" : "text-muted-foreground"
                                )}
                                aria-label={`Open ${d.toDateString()}`}
                            >
                                {d.getDate()}
                            </button>
                            {dayEvents.slice(0, 3).map((e) => (
                                <button
                                    key={e.key}
                                    type="button"
                                    onClick={(ev) => { ev.stopPropagation(); onOpen(e); }}
                                    className={cn("truncate rounded border-l-2 px-1 text-left text-[10px] leading-4", eventClasses(e))}
                                    title={`${e.title} · ${e.allDay ? "all day" : timeRange(e.start, e.end)}`}
                                >
                                    {e.allDay ? "" : `${e.start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} `}{e.title}
                                </button>
                            ))}
                            {dayEvents.length > 3 ? (
                                <button
                                    type="button"
                                    onClick={(ev) => { ev.stopPropagation(); onPickDay(d); }}
                                    className="text-left text-[10px] font-medium text-primary hover:underline"
                                >
                                    +{dayEvents.length - 3} more
                                </button>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Details ──────────────────────────────────────────────────────────────────

/** Close on Escape — the details dialogs are plain overlays, not Radix. */
function useEscape(onClose: () => void) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
}

/** An entry from the user's Mail calendar — read-only here; edited in Mail. */
function MailEventDetails({ event, tz, onClose }: { event: MailCalendarEvent; tz: string; onClose: () => void }) {
    useEscape(onClose);
    const start = new Date(event.startUtc);
    const end = new Date(event.endUtc);
    const source = event.source === "invite" ? "Invitation received by email"
        : event.source === "email" ? "Found in your email"
            : "Your event";
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Mail calendar event: ${event.subject}`}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border bg-card shadow-2xl">
                <div className="h-1.5 bg-indigo-500" />
                <div className="space-y-3 px-5 py-4">
                    <div className="flex items-start gap-2">
                        <h2 className={cn("min-w-0 flex-1 break-words text-base font-semibold", event.status === "cancelled" && "line-through")}>
                            {event.subject}
                        </h2>
                        <button type="button" onClick={onClose} autoFocus className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-accent" aria-label="Close">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 shrink-0" />
                        {event.isAllDay
                            ? `${formatDay(start, tz)} · All day`
                            : `${formatDay(start, tz)} · ${formatTime(start, tz)} – ${formatTime(end, tz)}`}
                    </p>
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                        <span className="flex items-center gap-1 rounded-full bg-indigo-500/15 px-2 py-0.5 font-medium text-indigo-700 dark:text-indigo-300">
                            <Mail className="h-3 w-3" aria-hidden /> Mail calendar
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{source}</span>
                        {event.isRecurring ? (
                            <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground"><Repeat className="h-3 w-3" aria-hidden /> Repeats</span>
                        ) : null}
                        {event.status === "cancelled" ? <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-destructive">Cancelled</span> : null}
                        {event.response && event.response !== "needs-action" ? (
                            <span className="rounded-full bg-muted px-2 py-0.5 capitalize text-muted-foreground">{event.response}</span>
                        ) : null}
                    </div>
                    {event.location ? (
                        <p className="flex items-start gap-2 text-sm">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                            <span className="min-w-0 whitespace-pre-wrap break-words">{event.location}</span>
                        </p>
                    ) : null}
                    {event.organizer ? <p className="text-xs text-muted-foreground">Organiser: {event.organizer}</p> : null}
                    {event.description ? (
                        <p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 px-3 py-2 text-sm">{event.description}</p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">Read-only here — change it in Mail.</p>
                </div>
                <footer className="flex flex-wrap items-center gap-2 border-t px-5 py-3">
                    <a
                        href={`${mailWebUrl}/calendar`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    >
                        <ExternalLink className="h-3.5 w-3.5" /> Open in Mail
                    </a>
                    {event.sourceMessageId ? (
                        <a
                            href={mailMessageUrl(event.sourceMessageId)}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs hover:bg-accent"
                        >
                            <Mail className="h-3.5 w-3.5" /> Open email
                        </a>
                    ) : null}
                </footer>
            </div>
        </div>
    );
}

function MeetingDetails({
    meeting,
    isHost,
    token,
    tz,
    myEmail,
    onClose,
    onEdit,
    onJoin
}: {
    meeting: Meeting;
    isHost: boolean;
    token: string | undefined;
    tz: string;
    myEmail: string;
    onClose: () => void;
    onEdit: () => void;
    onJoin: () => Promise<void>;
}) {
    useEscape(onClose);
    const cancel = useCancelMeeting();
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [copied, setCopied] = useState(false);
    const [joining, setJoining] = useState(false);
    const start = new Date(meeting.scheduledStart);
    const end = meeting.scheduledEnd ? new Date(meeting.scheduledEnd) : new Date(start.getTime() + 30 * 60_000);
    const closed = meeting.status === "Ended" || meeting.status === "Cancelled";
    const joinUrl = `${window.location.origin}/guest/${meeting.id}`;
    // Teams' meeting tabs: the details, the meeting chat (before, during and
    // after), the shared meeting notes, and — once it has started — the Recap.
    const [tab, setTab] = useState<"details" | "chat" | "notes" | "recap">("details");
    const hasStarted = meeting.status !== "Cancelled"
        && (meeting.status !== "Scheduled" || start.getTime() <= Date.now());
    const tabs = [
        { id: "details" as const, label: "Details" },
        { id: "chat" as const, label: "Chat" },
        { id: "notes" as const, label: "Notes" },
        ...(hasStarted ? [{ id: "recap" as const, label: "Recap" }] : [])
    ];

    // Teams' Tracking pane and RSVP: who was invited and how they answered.
    const qc = useQueryClient();
    const attendees = useQuery({
        queryKey: ["meeting-attendees", meeting.id, token],
        queryFn: () => connectParityApi.meetingInvitees(meeting.id, token),
        enabled: Boolean(token)
    });
    const mine = (attendees.data ?? []).find((a) => a.email.toLowerCase() === myEmail);
    const [rsvpBusy, setRsvpBusy] = useState(false);
    const answer = async (response: "Accepted" | "Tentative" | "Declined") => {
        setRsvpBusy(true);
        try {
            await connectParityApi.rsvpMeeting(meeting.id, response, token);
            await qc.invalidateQueries({ queryKey: ["meeting-attendees", meeting.id] });
        } catch { /* the pills show the last saved answer */ } finally {
            setRsvpBusy(false);
        }
    };
    const [forwardOpen, setForwardOpen] = useState(false);
    const [forwardTo, setForwardTo] = useState("");
    const [forwarding, setForwarding] = useState(false);
    const forward = async () => {
        const email = forwardTo.trim();
        if (!email.includes("@") || forwarding) return;
        setForwarding(true);
        try {
            await connectParityApi.inviteToMeeting(meeting.id, [email], token);
            setForwardTo("");
            setForwardOpen(false);
            await qc.invalidateQueries({ queryKey: ["meeting-attendees", meeting.id] });
        } catch { /* keep the address so it can be retried */ } finally {
            setForwarding(false);
        }
    };

    const downloadIcs = async () => {
        const res = await fetch(connectParityApi.meetingIcsUrl(meeting.id), {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "omit"
        });
        if (!res.ok) return;
        const url = URL.createObjectURL(await res.blob());
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = `${meeting.subject.replace(/[^a-z0-9_\- ]/gi, "_").slice(0, 60) || "meeting"}.ics`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } finally {
            URL.revokeObjectURL(url);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Meeting: ${meeting.subject}`}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border bg-card shadow-2xl">
                <div className={cn("h-1.5", meeting.status === "Live" ? "bg-emerald-500" : closed ? "bg-muted-foreground/40" : "bg-primary")} />
                <div className="space-y-3 px-5 py-4">
                    <div className="flex items-start gap-2">
                        <h2 className={cn("min-w-0 flex-1 break-words text-base font-semibold", meeting.status === "Cancelled" && "line-through")}>
                            {meeting.subject}
                        </h2>
                        <button type="button" onClick={onClose} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-accent" aria-label="Close">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div role="tablist" aria-label="Meeting" className="flex gap-1 border-b">
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={tab === t.id}
                                onClick={() => setTab(t.id)}
                                className={cn(
                                    "-mb-px border-b-2 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                                    tab === t.id ? "border-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
                                )}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {tab === "details" ? (
                    <>
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 shrink-0" />
                        {formatDay(start, tz)} · {formatTime(start, tz)} – {formatTime(end, tz)}
                    </p>
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                        <span className={cn("rounded-full px-2 py-0.5 font-medium", statusBadge(meeting.status))}>{meeting.status}</span>
                        {isHost ? <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">You're the organiser</span> : null}
                    </div>
                    {meeting.description ? (
                        <p className="whitespace-pre-wrap rounded-md bg-muted/40 px-3 py-2 text-sm">{meeting.description}</p>
                    ) : null}

                    {!isHost && mine && !closed ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground">Your response</span>
                            <div role="group" aria-label="RSVP" className="flex overflow-hidden rounded-md border">
                                {(["Accepted", "Tentative", "Declined"] as const).map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        disabled={rsvpBusy}
                                        onClick={() => void answer(r)}
                                        aria-pressed={mine.response === r}
                                        className={cn(
                                            "h-8 border-r px-3 text-xs font-medium last:border-r-0 disabled:opacity-60",
                                            mine.response === r
                                                ? r === "Accepted" ? "bg-emerald-600 text-white" : r === "Tentative" ? "bg-amber-500 text-white" : "bg-rose-600 text-white"
                                                : "hover:bg-accent"
                                        )}
                                    >
                                        {r === "Accepted" ? "Accept" : r === "Tentative" ? "Tentative" : "Decline"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <section aria-label="Tracking" className="space-y-1.5">
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Tracking {attendees.data ? `· ${attendees.data.length + 1} invited` : ""}
                        </h3>
                        <ul className="max-h-48 space-y-1 overflow-y-auto">
                            <li className="flex items-center gap-2 text-sm">
                                <ChatAvatar name={isHost ? "You" : "Organiser"} size="sm" />
                                <span className="min-w-0 flex-1 truncate">{isHost ? "You" : "Organiser"}</span>
                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">Organiser</span>
                            </li>
                            {attendees.isPending ? (
                                <li className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</li>
                            ) : (attendees.data ?? []).map((a) => (
                                <li key={a.id} className="flex items-center gap-2 text-sm">
                                    <ChatAvatar name={a.displayName ?? a.email} size="sm" />
                                    <span className="min-w-0 flex-1 truncate" title={a.email}>
                                        {a.displayName ?? a.email}{a.email.toLowerCase() === myEmail ? " (you)" : ""}
                                    </span>
                                    <ResponsePill response={a.response} />
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* Teams: the meeting's recordings (saved to the organizer's Drive, shared with attendees). */}
                    <MeetingRecordingsSection meetingId={meeting.id} />

                    {forwardOpen && !closed ? (
                        <form onSubmit={(e) => { e.preventDefault(); void forward(); }} className="flex gap-2">
                            <input
                                type="email"
                                autoFocus
                                value={forwardTo}
                                onChange={(e) => setForwardTo(e.target.value)}
                                placeholder="Invite someone by email"
                                aria-label="Invite someone by email"
                                className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            />
                            <button
                                type="submit"
                                disabled={!forwardTo.includes("@") || forwarding}
                                className="flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-50"
                            >
                                {forwarding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Send
                            </button>
                        </form>
                    ) : null}

                    {confirmCancel ? (
                        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
                            <p>Cancel this meeting for everyone? Attendees get a cancellation that removes it from their calendar.</p>
                            <div className="mt-2 flex justify-end gap-2">
                                <button type="button" onClick={() => setConfirmCancel(false)} className="h-8 rounded-md border px-3 text-xs hover:bg-accent">
                                    Keep it
                                </button>
                                <button
                                    type="button"
                                    disabled={cancel.isPending}
                                    onClick={() => cancel.mutate(meeting.id, { onSuccess: onClose })}
                                    className="flex h-8 items-center gap-1.5 rounded-md bg-destructive px-3 text-xs font-medium text-destructive-foreground disabled:opacity-50"
                                >
                                    {cancel.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                    Cancel meeting
                                </button>
                            </div>
                            {cancel.isError ? <p className="mt-1 text-xs text-destructive">Couldn't cancel — try again.</p> : null}
                        </div>
                    ) : null}
                    </>
                    ) : tab === "chat" ? (
                        <div className="-mx-5 flex h-[55vh] min-h-[320px] flex-col border-b" role="tabpanel" aria-label="Meeting chat">
                            <MeetingChatThread meetingId={meeting.id} title={meeting.subject} variant="panel" />
                        </div>
                    ) : tab === "notes" ? (
                        <div role="tabpanel" aria-label="Meeting notes">
                            <MeetingNotes meetingId={meeting.id} />
                        </div>
                    ) : (
                        <div role="tabpanel" aria-label="Recap">
                            <MeetingRecap meetingId={meeting.id} />
                        </div>
                    )}
                </div>
                <footer className="flex flex-wrap items-center gap-2 border-t px-5 py-3">
                    {!closed ? (
                        <button
                            type="button"
                            disabled={joining}
                            onClick={async () => { setJoining(true); try { await onJoin(); } finally { setJoining(false); } }}
                            className="flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-50"
                        >
                            {joining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />}
                            {meeting.status === "Live" ? "Join now" : "Join"}
                        </button>
                    ) : null}
                    {isHost && !closed ? (
                        <>
                            <button type="button" onClick={onEdit} className="flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs hover:bg-accent">
                                <Pencil className="h-3.5 w-3.5" /> Edit
                            </button>
                            <button
                                type="button"
                                onClick={() => setConfirmCancel(true)}
                                className="flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs text-destructive hover:bg-destructive/10"
                            >
                                <XCircle className="h-3.5 w-3.5" /> Cancel meeting
                            </button>
                        </>
                    ) : null}
                    <div className="ml-auto flex items-center gap-1">
                        {!closed ? (
                            <button
                                type="button"
                                onClick={() => setForwardOpen((v) => !v)}
                                aria-pressed={forwardOpen}
                                className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                                title="Forward — invite more people"
                                aria-label="Forward — invite more people"
                            >
                                <Forward className="h-4 w-4" />
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={async () => { try { await navigator.clipboard.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } }}
                            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                            title={copied ? "Copied" : "Copy join link"}
                            aria-label="Copy join link"
                        >
                            <Copy className="h-4 w-4" />
                        </button>
                        {/* Add to any calendar: the .ics file (any calendar app) or a one-click link for web calendars — the same set the invitation
                            email carries, for people whose mail app didn't file the invite. */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                                    title="Add to calendar"
                                    aria-label="Add to calendar"
                                >
                                    <Download className="h-4 w-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs">Add to calendar</DropdownMenuLabel>
                                <DropdownMenuItem onSelect={() => void downloadIcs()}>
                                    <CalendarDays className="mr-2 h-4 w-4" /> Calendar file (.ics)
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {calendarLinks({
                                    id: meeting.id,
                                    subject: meeting.subject,
                                    start: meeting.scheduledStart,
                                    end: meeting.scheduledEnd ?? null,
                                    description: meeting.description ?? null,
                                    joinUrl,
                                    apiBase: window.location.origin
                                }).filter((l) => l.id !== "ics").map((l) => (
                                    <DropdownMenuItem key={l.id} asChild>
                                        <a href={l.href} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="mr-2 h-4 w-4" /> {l.label}
                                        </a>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </footer>
            </div>
        </div>
    );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function ResponsePill({ response }: { response: string }) {
    const style = response === "Accepted" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        : response === "Tentative" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
        : response === "Declined" ? "bg-rose-500/15 text-rose-700 dark:text-rose-300"
        : "bg-muted text-muted-foreground";
    const label = response === "Pending" ? "No response" : response;
    return <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", style)}>{label}</span>;
}

function EventIcon({ ev }: { ev: CalEvent }) {
    if (ev.mail) return <Mail className="h-3 w-3 shrink-0" aria-label="Mail calendar" />;
    return ev.meeting?.status === "Live" ? <Radio className="h-3 w-3 shrink-0" /> : <Video className="h-3 w-3 shrink-0" />;
}

function eventClasses(ev: CalEvent): string {
    if (ev.meeting) return eventTone(ev.meeting.status);
    const e = ev.mail;
    const dim = e?.status === "cancelled" || e?.response === "declined";
    return cn(
        "border border-dashed border-l-indigo-500 border-indigo-500/60 bg-indigo-500/10 text-indigo-800 dark:text-indigo-200",
        dim && "opacity-60",
        e?.status === "cancelled" && "line-through"
    );
}

function eventTone(status: Meeting["status"]): string {
    switch (status) {
        case "Live": return "border-emerald-600 bg-emerald-500/90 text-white";
        case "Ended":
        case "Cancelled": return "border-muted-foreground/40 bg-muted text-muted-foreground line-through";
        default: return "border-primary bg-primary/15 text-primary";
    }
}

function statusBadge(status: Meeting["status"]): string {
    switch (status) {
        case "Live": return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
        case "Cancelled": return "bg-destructive/10 text-destructive";
        case "Ended": return "bg-muted text-muted-foreground";
        default: return "bg-primary/10 text-primary";
    }
}

function startOfDay(d: Date): Date {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    return out;
}

/** Monday-based week, as in the Mail calendar. */
function startOfWeek(d: Date): Date {
    const out = startOfDay(d);
    out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
    return out;
}

function addDays(d: Date, n: number): Date {
    const out = new Date(d);
    out.setDate(d.getDate() + n);
    return out;
}

function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function minutesOf(d: Date): number {
    return d.getHours() * 60 + d.getMinutes();
}

function formatHour(h: number): string {
    if (h === 0) return "12 AM";
    if (h < 12) return `${h} AM`;
    if (h === 12) return "12 PM";
    return `${h - 12} PM`;
}

/** For zoned-wall Dates: their local fields already are the calendar zone's clock. */
function timeRange(a: Date, b: Date): string {
    const o: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
    return `${a.toLocaleTimeString(undefined, o)} – ${b.toLocaleTimeString(undefined, o)}`;
}

function formatRange(a: Date, b: Date): string {
    const sameMonth = a.getMonth() === b.getMonth();
    const month = a.toLocaleString(undefined, { month: "short" });
    if (sameMonth) return `${month} ${a.getDate()} – ${b.getDate()}, ${a.getFullYear()}`;
    return `${a.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${b.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}
